import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import classNames from 'classnames';
import noop from 'lodash-es/noop';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import React, { Component } from 'react';
import caret from '../../assets/svg/caretOrange.svg';
import deleteIcon from '../../assets/svg/remove.svg';
import { ElemType, MapClassesToElem } from '../../helpers/styles-helpers';
import { Card } from '../card';
import { CollapsibleCardNotification } from './collapsible-card-notification';

const noBorderClasses = ['noBorders', 'noBordersTop'];

class CollapsibleCard extends Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    children: PropTypes.node.isRequired,
    content: PropTypes.node.isRequired,
    className: PropTypes.string,
    contentRight: PropTypes.node,
    dataCy: PropTypes.string,
    initialOpen: PropTypes.bool,
    forceOpen: PropTypes.bool,
    onChange: PropTypes.func,
    type: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'noBorders', 'noBordersTop']),
    showChevron: PropTypes.bool,
    /**
     * Controlled open state. MUST be paired with onChange — otherwise local state can drift
     * from the prop and snap back unexpectedly on unrelated re-renders.
     * Use initialOpen instead if you want uncontrolled behavior.
     */
    open: PropTypes.bool,
    keepContentMounted: PropTypes.bool,
    isRemovable: PropTypes.bool,
    onDelete: PropTypes.func,
    disableRemoveButton: PropTypes.bool,
    collapsedSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  };

  static defaultProps = {
    type: 'primary',
    dataCy: 'Collapsible',
    contentRight: null,
    initialOpen: false,
    onChange: noop,
    forceOpen: false,
    className: '',
    showChevron: true,
    open: false,
    keepContentMounted: false,
    isRemovable: false,
    onDelete: noop,
    disableRemoveButton: false,
    collapsedSize: undefined
  };

  constructor(props) {
    super(props);
    this.state = {
      open: props.open || props.initialOpen
    };
  }

  toggle = () =>
    this.setState(
      ({ open }) => ({ open: !open }),
      () => this.props.onChange && this.props.onChange(this.state.open)
    );

  // this function can open a collapsible card "remotely" - added for New Construction checkbox on QuoteForms
  componentDidUpdate(prevProps) {
    if (this.props.forceOpen !== prevProps.forceOpen) {
      this.setState({ open: this.props.forceOpen });
    }

    if (this.props.open !== prevProps.open) {
      this.setState({ open: this.props.open });
    }
  }

  render() {
    const { state, toggle } = this;
    const { open } = state;
    const {
      classes,
      content,
      enclosedCard,
      contentRight,
      invertOpenClose,
      closedContentRight,
      openContentRight,
      children,
      dataCy,
      type,
      initialOpen,
      forceOpen,
      collapsibleCardNotification,
      className,
      showChevron,
      keepContentMounted,
      isRemovable,
      onDelete,
      disableRemoveButton,
      collapsedSize,
      // Strip these from the spread so they don't leak onto the underlying <Card> div.
      // `onChange` in particular would otherwise be attached as a synthetic event handler and
      // fire on every bubbled change event from descendant inputs/selects, causing the card to
      // toggle when the user types in an unrelated child form field.
      open: _openProp,
      onChange: _onChange,
      ...props
    } = this.props;
    const noBorder = noBorderClasses.includes(type);
    const inverseContentRightText = !open ? closedContentRight : openContentRight;
    return (
      <Collapse
        in={open}
        collapsedSize={collapsedSize ?? (noBorder ? 70 : collapsibleCardNotification ? 130 : 80)}
        className={classNames(classes.collapse, { [classes.noBorders]: noBorder })}
      >
        <Card
          className={classNames(classes.container, className, { [classes.containerOpen]: open })}
          type={type}
          {...props}
        >
          <div className={classes.buttonContainer}>
            {isRemovable && (
              <IconButton
                classes={MapClassesToElem(ElemType.IconButton, classes)}
                onClick={onDelete}
                data-cy={`${dataCy}-delete`}
                disabled={disableRemoveButton}
              >
                <img className={classes.deleteIcon} src={deleteIcon} alt="delete" />
              </IconButton>
            )}
            {showChevron && (
              <IconButton
                classes={MapClassesToElem(ElemType.IconButton, classes)}
                onClick={toggle}
                data-cy={`${dataCy}-toggle`}
              >
                <img className={open ? classes.icon : classes.iconFold} src={caret} alt={open ? 'fold' : 'unfold'} />
              </IconButton>
            )}
          </div>
          <Grid>
            <Grid className={classes.header}>
              {content && !enclosedCard && <Grid className={classes.content}>{content}</Grid>}
              {enclosedCard && <Grid className={classes.enclosedCard}>{content}</Grid>}
              {contentRight && <Grid className={classes.contentRight}>{contentRight}</Grid>}
              {invertOpenClose && <Grid className={classes.contentRight}>{inverseContentRightText}</Grid>}
            </Grid>
            {collapsibleCardNotification && (
              <CollapsibleCardNotification open={open}>{collapsibleCardNotification}</CollapsibleCardNotification>
            )}
          </Grid>
          {keepContentMounted ? (
            <div className={classNames({ [classes.hiddenContent]: !open })}>{children}</div>
          ) : (
            open && children
          )}
        </Card>
      </Collapse>
    );
  }
}

export default CollapsibleCard;
